import { useState } from "react";
import { Button, Dialog } from "@hybrid-iptv/ui";
import { useProfiles } from "./profileStore.js";
import { useSubscriptionStore } from "../social/subscriptions.js";
import { disconnect, useYoutubeConnection } from "../social/youtubeAccount.js";

/**
 * Settings → YouTube account (build guide docs/social-connect-youtube.md, Phase 2b): shown only
 * for a profile that has connected its YouTube, to disconnect it. Connecting happens on the
 * Social page, so a profile that has not connected, a Kids profile, the website and the builds
 * without the sign-in show nothing here.
 *
 * Disconnect asks first (Cancel is focused), then revokes the app's access at Google and
 * deletes the sign-in from this TV.
 */
export function YoutubeAccountCard() {
  const profile = useProfiles((state) => state.profiles.find((p) => p.id === state.activeProfileId));
  const { available, connected } = useYoutubeConnection(profile?.id);
  // The account's channel name, once the Social page has read it (an account without one shows none).
  const accountName = useSubscriptionStore((state) => (profile ? state.byProfile[profile.id]?.accountName : undefined));
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!profile || profile.isKids || available !== true || connected !== true) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await disconnect(profile.id);
    } finally {
      setBusy(false);
      setAsking(false);
    }
  };

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">YouTube account</h2>
      <p className="settings-card-desc">
        {profile.name} is connected to YouTube. SliqueTV only reads the channels you subscribe to; it never posts, likes
        or subscribes.
      </p>
      <div className="settings-row">
        <span className="settings-row-label">{accountName ? `Connected as ${accountName}` : "Connected"}</span>
        <button type="button" className="ui-btn" onClick={() => setAsking(true)} disabled={busy}>
          Disconnect
        </button>
      </div>
      <Dialog
        open={asking}
        title={`Disconnect YouTube from ${profile.name}?`}
        onClose={() => setAsking(false)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void confirm()} disabled={busy}>
              Disconnect
            </Button>
          </>
        }
      >
        SliqueTV will stop reading this profile&apos;s subscriptions, and its access to the account is removed at Google
        and from this TV. You can connect again from the Social page.
      </Dialog>
    </div>
  );
}
